import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Camera } from 'lucide-react';

import { useDispatch, useSelector } from 'react-redux';
import { selectUserToken } from '../../../../redux/features/user/userSelectors';
import {
  useGetRequestQuery,
  useUpdateRequestMutation,
} from '../../../../redux/services/requests/requestsApi';
import {
  setSearchRequestId,
  showSearch,
} from '../../../../redux/features/dashboard/dashboardSlice';

import { ItemHeader } from '../ItemHeader';
import { ItemFooter } from '../ItemFooter';
import { ClientsInfoBlock } from '../../../form/search/clients/ClientsInfoBlock';
import { RequestBadges } from '../../../form/search/requests/RequestBadges';
import { RequestNote } from '../../../form/search/requests/RequestNote';

import { colors, errorMessages } from '../../../../data/constants';
import { showError } from '../../../../utils/ui';
import { getDefaultValuesForEditing } from '../../../../utils/data';
import { formatNumberDisplay } from '../../../../utils/utils';

export const DealsItem = ({ reminder = {}, group, setIsLoading = () => {} }) => {
  const { id, agent, subject, created } = reminder;
  // BE sends null for a client/request the viewer may not see (e.g. as assistant)
  const client = reminder.client ?? {};
  const client_request = reminder.client_request ?? {};
  const isClientHidden = !reminder.client;
  const { client_type, email, phone, locale, first_name, last_name } = client;
  const {
    id: requestId,
    urgency_type,
    status,
    created_at,
    comment,
    note,
    agent_name,
    budget_from,
    budget_to,
    realty_id,
  } = client_request;
  const budgetFrom = budget_from ? `${formatNumberDisplay(budget_from)} €` : '';
  const budgetTo = budget_to ? `${formatNumberDisplay(budget_to)} €` : '';

  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [selectedRequestValues, setSelectedRequestValues] = useState(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userToken = useSelector(selectUserToken);
  const {
    data: clientRequest = {},
    isLoading: isClientsRequestLoading,
    error: clientsRequestError,
  } = useGetRequestQuery(
    {
      token: userToken,
      id: selectedRequestId,
    },
    { skip: !selectedRequestId, refetchOnMountOrArgChange: true }
  );
  const [editRequest, { isLoading: isEditingLoading, error: editingError }] =
    useUpdateRequestMutation();

  const handleSubmitEditing = async () => {
    const newRequest = await getDefaultValuesForEditing(clientRequest);

    const response = await editRequest({
      token: userToken,
      id: requestId,
      data: { ...newRequest, ...selectedRequestValues },
    });

    const errors = editingError || response?.error;

    showError(errors, errorMessages.wentWrong);
    setSelectedRequestId(null);
  };

  const handleEditRequest = values => {
    setSelectedRequestId(requestId);
    setSelectedRequestValues(values);
  };

  const handleRequestIdClick = async () => {
    await dispatch(setSearchRequestId(requestId));
  };

  const handleMatchingClick = () => {
    navigate(`/?requestId=${requestId}`);
    dispatch(showSearch({ isSearchVisible: true, searchQuery: '' }));
  };

  const handleRealtyIdClick = () => {
    navigate('/');

    dispatch(
      showSearch({
        isSearchVisible: true,
        searchQuery: '',
        searchFilters: `?search_type=1&id=${realty_id}`,
      })
    );
  };

  useEffect(() => {
    if (selectedRequestId && clientRequest && !isClientsRequestLoading) {
      handleSubmitEditing();
    }
  }, [selectedRequestId, isClientsRequestLoading, clientRequest]);

  return (
    <div className="flex flex-col gap-2 p-2 w-full h-full min-w-0 bg-gray-100 rounded">
      <ItemHeader reminder={reminder} group={group} />

      {client && client_request && (
        <>
          <div className="flex gap-2 w-full">
            <div className="flex justify-center items-center p-1 w-10 h-10 bg-whiteColor rounded">
              <Camera
                size={26}
                color={colors.darkGrayColor}
              />
            </div>

            {isClientHidden ? (
              <span className="text-sm text-darkGrayColor">
                Client of another agent
              </span>
            ) : (
              <ClientsInfoBlock
                email={email}
                phone={phone}
                id={id}
                firstName={first_name}
                lastName={last_name}
                clientType={client_type?.title?.toLowerCase()}
                language={locale?.title}
              />
            )}
          </div>

          <div className="flex gap-2 w-full">
            <div className="flex flex-col">
              <span className="block text-[10px] text-blackColor">
                {created_at}
              </span>

              <div onClick={handleRequestIdClick}>
                <Link
                  to="/clients"
                  rel="noopener noreferrer"
                >
                  <span className="text-xs text-darkBlueColor underline cursor-pointer">
                    {requestId}
                  </span>
                </Link>
              </div>

              <div onClick={handleMatchingClick}>
                <span className="text-xs text-darkBlueColor underline cursor-pointer">
                  Matching
                </span>
              </div>
            </div>

            <RequestBadges
              agent_name={agent_name}
              status={status}
              urgency_type={urgency_type}
              handleSubmitEditing={handleEditRequest}
            />
          </div>

          <div className="flex gap-2 items-center w-full">
            <span className="block text-[10px] text-blackColor">
              {budgetFrom && budgetTo
                ? `${budgetFrom} - ${budgetTo}`
                : budgetFrom || budgetTo}
            </span>

            {realty_id && (
              <div className="cursor-pointer" onClick={handleRealtyIdClick}>
                <span className="text-xs text-darkBlueColor underline">
                  {realty_id}
                </span>
              </div>
            )}
          </div>

          <div className="relative w-full">
            <RequestNote
              handleSubmitEditing={handleSubmitEditing}
              agent_name={agent_name}
              formatedDate={created_at}
              comment={comment}
              note={note}
            />
          </div>
        </>
      )}

      <ItemFooter
        agent={agent.name}
        subject={subject}
        created={created}
      />
    </div>
  );
};
